import { useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Popover,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import { AppInsight, ThreatFinding, SecurityFinding } from '@/types';
import { Virtuoso } from 'react-virtuoso';
import { MarkdownContent } from './DataDisplayWidgets';
import { SmartDiagram } from './DiagramComponents';
import {
  SECURITY_CATEGORY_DISPLAY,
  normalizeFindingCategory,
} from './constants';
import { SecurityFindingsFilter, FindingFilterType } from './SecurityFindingsFilter';
import { SecurityFindingAccordion } from './SecurityFindingAccordion';
import { CategoryRecommendationsDialog } from './CategoryRecommendationsDialog';
import { SecurityTaskDialog } from './SecurityTaskDialog';
import sentinelNetwork from '@/lib/api/sentinelNetwork';

interface SecurityScoreCardProps {
  score: AppInsight['securityScore'] & {};
  appId: string;
  onAssignTask?: (findingId: string) => void;
  findingsFilter: FindingFilterType;
  setFindingsFilter: (filter: FindingFilterType) => void;
  expandedFindingPanel: string | false;
  onFindingAccordionChange: (panel: string) => (event: React.SyntheticEvent, isExpanded: boolean) => void;
  isTaskDisabled?: boolean;
  threatModelDiagram?: string;
  threatFindings?: ThreatFinding[];
  hideFindings?: boolean;
  hideScoreBreakdown?: boolean;
}

function SecurityScoreCard({ 
  score, 
  appId, 
  onAssignTask, 
  findingsFilter, 
  setFindingsFilter,
  expandedFindingPanel,
  onFindingAccordionChange,
  isTaskDisabled = false,
  threatModelDiagram,
  threatFindings = [],
  hideFindings = false,
  hideScoreBreakdown = false,
}: SecurityScoreCardProps) {
  const scoreColor = (v: number) => v >= 90 ? '#22c55e' : v >= 70 ? '#3b82f6' : v >= 50 ? '#f59e0b' : v >= 25 ? '#f97316' : '#ef4444';
  const ratingColor = (r: string) => r === 'A' ? '#22c55e' : r === 'B' ? '#3b82f6' : r === 'C' ? '#f59e0b' : r === 'D' ? '#f97316' : '#ef4444';
  
  // State for categories popover
  const [categoriesAnchor, setCategoriesAnchor] = useState<HTMLElement | null>(null);
  const categoriesOpen = Boolean(categoriesAnchor);

  // State for category recommendations dialog
  const [selectedCategory, setSelectedCategory] = useState<{ key: string; displayName: string; score: number } | null>(null);
  const categoryDialogOpen = Boolean(selectedCategory);

  const handleCategoryClick = (categoryKey: string, categoryScore: number) => {
    const displayName = SECURITY_CATEGORY_DISPLAY[categoryKey] || categoryKey.replace(/([A-Z])/g, ' $1').trim();
    setSelectedCategory({ key: categoryKey, displayName, score: categoryScore });
  };

  // Generate findings from criticalFindings if findings array is not available
  // Normalize severity to lowercase AND category to canonical key on every finding
  const securityFindings: SecurityFinding[] = (score?.findings || 
    (score?.criticalFindings || []).map((finding, index) => ({
      id: `finding-${index}`,
      category: 'Security',
      finding: finding,
      severity: 'critical' as const,
      recommendations: score?.topRecommendations?.slice(0, 2),
    }))).map(f => ({
      ...f,
      severity: (f.severity || '').toLowerCase() as any,
      category: normalizeFindingCategory(f.category || 'uncategorized'),
    }));

  // Convert threat findings to security findings format and merge
  const convertedThreatFindings: SecurityFinding[] = threatFindings.map((tf, index) => ({
    id: `threat-${tf.id || index}`,
    category: tf.category || 'Threat',
    finding: tf.finding,
    severity: tf.severity,
    details: tf.details,
    evidence: tf.evidence?.map(e => ({ file: e.file, line_range: e.line_range, description: e.description })),
    recommendations: tf.status === 'POTENTIAL' ? tf.recommendations : tf.mitigations,
  }));

  // Combine all findings
  const findings = [...securityFindings, ...convertedThreatFindings];

  // Filter findings based on severity
  const filteredFindings = findings.filter((f) => {
    if (findingsFilter === 'ALL') return true;
    return f.severity === findingsFilter.toLowerCase();
  });

  // Assign Task dialog state
  const [activeTaskFinding, setActiveTaskFinding] = useState<SecurityFinding | null>(null);
  const [taskSnackbar, setTaskSnackbar] = useState<{ open: boolean; severity: 'success' | 'error'; message: string }>({
    open: false,
    severity: 'success',
    message: '',
  });

  const handleAssignTaskClick = (findingId: string) => {
    const finding = findings.find((f) => f.id === findingId);
    if (finding) setActiveTaskFinding(finding);
  };

  const handleTaskSubmit = async (data: {
    findingId: string;
    appId: string;
    task: string;
    taskType: 'fix' | 'reanalyse' | 'mark-resolved';
    resolvedJustification?: string;
  }) => {
    try {
      await sentinelNetwork.post('/task-agent', data);
      setTaskSnackbar({
        open: true,
        severity: 'success',
        message: data.taskType === 'mark-resolved' ? 'Finding marked as resolved.' : 'Task submitted successfully.',
      });
      onAssignTask?.(data.findingId);
    } catch (err: any) {
      setTaskSnackbar({
        open: true,
        severity: 'error',
        message: err?.response?.data?.message || err?.message || 'Failed to submit task.',
      });
      throw err;
    }
  };

  const theme = useTheme();
  const overallScore = score.overall ?? 0;
  const overallRating = score.rating || (overallScore >= 90 ? 'A' : overallScore >= 70 ? 'B' : overallScore >= 50 ? 'C' : overallScore >= 25 ? 'D' : 'E');
  const overallColor = scoreColor(overallScore);
  const breakdownEntries = Object.entries(score.breakdown || {});
  const boxCount = 10;

  // Technical debt display
  const techDebtMinutes = score.technicalDebtMinutes || findings.reduce((sum, f) => sum + (f.effortMinutes || 0), 0);
  const techDebtDisplay = techDebtMinutes >= 480 ? `${Math.round(techDebtMinutes / 480)}d` : techDebtMinutes >= 60 ? `${Math.round(techDebtMinutes / 60)}h` : `${techDebtMinutes}m`;

  // Half-donut geometry (0-100 scale)
  const gaugeW = 180, gaugeH = 100;
  const cx = gaugeW / 2, cy = 95;
  const outerR = 80, innerR = 58;
  const dotCount = 24;
  const filledDots = Math.round((overallScore / 100) * dotCount);

  return (
    <Stack spacing={2}>
      {/* Top Section - Half-donut gauge + Breakdown */}
      {!hideScoreBreakdown && (
      <Stack direction="row" spacing={3} alignItems="flex-start">

        {/* Half-donut gauge + rating badge */}
        <Tooltip title="Click to see security categories covered" arrow>
          <Box
            onClick={(e) => setCategoriesAnchor(e.currentTarget)}
            sx={{ flexShrink: 0, cursor: 'pointer', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.03)' }, textAlign: 'center' }}
          >
            <svg width={gaugeW} height={gaugeH + 10} viewBox={`0 0 ${gaugeW} ${gaugeH + 10}`}>
              {/* Outer arc dots */}
              {Array.from({ length: dotCount }).map((_, i) => {
                const angle = Math.PI + (i / (dotCount - 1)) * Math.PI;
                const x = cx + outerR * Math.cos(angle);
                const y = cy + outerR * Math.sin(angle);
                const active = i < filledDots;
                return (
                  <circle key={`o-${i}`} cx={x} cy={y} r={6}
                    fill={active ? overallColor : alpha(theme.palette.text.primary, 0.08)}
                    opacity={active ? 0.9 : 0.4}
                  >
                    {active && <animate attributeName="opacity" values="0.65;1;0.65" dur={`${2 + (i % 5) * 0.3}s`} repeatCount="indefinite" />}
                  </circle>
                );
              })}
              {/* Inner arc dots (every other) */}
              {Array.from({ length: dotCount }).filter((_, i) => i % 2 === 0).map((_, idx) => {
                const i = idx * 2;
                const angle = Math.PI + ((i + 0.5) / (dotCount - 1)) * Math.PI;
                const x = cx + innerR * Math.cos(angle);
                const y = cy + innerR * Math.sin(angle);
                const active = i < filledDots;
                return (
                  <circle key={`i-${idx}`} cx={x} cy={y} r={3.5}
                    fill={active ? overallColor : alpha(theme.palette.text.primary, 0.05)}
                    opacity={active ? 0.5 : 0.25}
                  />
                );
              })}
              {/* Center score text */}
              <text x={cx} y={cy - 8} textAnchor="middle" fill={overallColor} fontSize="28" fontWeight="800" fontFamily="Poppins, sans-serif">
                {overallScore}
              </text>
              <text x={cx} y={cy + 8} textAnchor="middle" fill={theme.palette.text.disabled} fontSize="8" fontWeight="600" letterSpacing="1.5" fontFamily="Poppins, sans-serif">
                / 100
              </text>
            </svg>
            {/* Rating badge + tech debt below gauge */}
            <Stack direction="row" spacing={1} justifyContent="center" alignItems="center" sx={{ mt: -0.5 }}>
              <Chip
                label={`Rating ${overallRating}`}
                size="small"
                sx={{
                  height: 22, fontSize: 11, fontWeight: 700,
                  bgcolor: alpha(ratingColor(overallRating), 0.12),
                  color: ratingColor(overallRating),
                  borderRadius: 1,
                }}
              />
              {techDebtMinutes > 0 && (
                <Tooltip title={`Technical debt: ~${techDebtMinutes} minutes to fix all findings`}>
                  <Chip
                    label={`Debt: ${techDebtDisplay}`}
                    size="small"
                    sx={{ height: 22, fontSize: 10, fontWeight: 600, bgcolor: alpha('#64748b', 0.08), color: '#64748b', borderRadius: 1 }}
                  />
                </Tooltip>
              )}
            </Stack>
          </Box>
        </Tooltip>

        {/* Categories Popover */}
        <Popover
          open={categoriesOpen}
          anchorEl={categoriesAnchor}
          onClose={() => setCategoriesAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
          transformOrigin={{ vertical: 'top', horizontal: 'center' }}
          PaperProps={{
            sx: {
              bgcolor: 'background.paper',
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              minWidth: 320,
              maxHeight: 400,
              overflow: 'auto',
            },
          }}
        >
          <Box sx={{ p: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.5, borderBottom: '1px solid', borderColor: 'divider', pb: 1 }}>
              Security Categories
            </Typography>
            <Stack spacing={0.75}>
              {breakdownEntries.map(([key, val]) => {
                const catData = typeof val === 'object' && val !== null ? val as { rating?: string; score?: number; findingCount?: number } : { score: val as number };
                const catRating = catData.rating || '';
                const catScore = catData.score ?? (typeof val === 'number' ? val : 0);
                const displayName = SECURITY_CATEGORY_DISPLAY[key] || key.replace(/([A-Z])/g, ' $1').trim();
                return (
                  <Stack key={key} direction="row" alignItems="center" spacing={1.5} sx={{ py: 0.5 }}>
                    {catRating && (
                      <Box sx={{
                        width: 22, height: 22, borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                        bgcolor: alpha(ratingColor(catRating), 0.12), color: ratingColor(catRating),
                        fontSize: 11, fontWeight: 700,
                      }}>
                        {catRating}
                      </Box>
                    )}
                    <Typography sx={{ fontSize: 12, flex: 1 }}>
                      {displayName}
                    </Typography>
                    <Typography sx={{ fontSize: 11, color: 'text.disabled', fontWeight: 600 }}>
                      {catScore}/100
                    </Typography>
                  </Stack>
                );
              })}
            </Stack>
          </Box>
        </Popover>

        {/* Breakdown — segmented box chart rows */}
        <Box sx={{ flex: 1 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.5 }}>Score Breakdown</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, columnGap: 3 }}>
            {breakdownEntries.map(([key, val]) => {
              const catData = typeof val === 'object' && val !== null ? val as { rating?: string; score?: number; findingCount?: number } : { score: val as number };
              const catScore = catData.score ?? (typeof val === 'number' ? val : 0);
              const catRating = catData.rating || '';
              const findingCount = (catData as any).findingCount ?? 0;
              const color = scoreColor(catScore);
              const filledBoxes = Math.round((catScore / 100) * boxCount);
              const displayName = SECURITY_CATEGORY_DISPLAY[key] || key.replace(/([A-Z])/g, ' $1').trim();
              return (
                <Tooltip key={key} title="Click to see recommendations" arrow placement="top">
                  <Box
                    onClick={() => handleCategoryClick(key, catScore)}
                    sx={{
                      cursor: 'pointer',
                      borderRadius: 1,
                      transition: 'background-color 0.2s',
                      '&:hover': { bgcolor: alpha(theme.palette.text.primary, 0.04) },
                      mx: -0.5,
                      px: 0.5,
                    }}
                  >
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Stack direction="row" spacing={0.5} alignItems="center">
                        {catRating && (
                          <Box sx={{
                            width: 16, height: 16, borderRadius: 0.5, display: 'flex', alignItems: 'center', justifyContent: 'center',
                            bgcolor: alpha(ratingColor(catRating), 0.12), color: ratingColor(catRating),
                            fontSize: 9, fontWeight: 700,
                          }}>
                            {catRating}
                          </Box>
                        )}
                        <Typography sx={{ color: 'text.secondary', fontSize: '0.7rem', fontWeight: 600, textTransform: 'capitalize' }}>
                          {displayName}
                        </Typography>
                      </Stack>
                      <Stack direction="row" spacing={0.5} alignItems="baseline">
                        <Typography sx={{ color, fontWeight: 700, fontSize: '0.85rem' }}>{catScore}</Typography>
                        <Typography sx={{ color: 'text.disabled', fontSize: '0.6rem', fontWeight: 500 }}>/100</Typography>
                        {findingCount > 0 && (
                          <Typography sx={{ color: 'text.disabled', fontSize: '0.55rem', fontWeight: 500 }}>
                            ({findingCount})
                          </Typography>
                        )}
                      </Stack>
                    </Box>
                    <Box sx={{ display: 'flex', gap: '3px' }}>
                      {Array.from({ length: boxCount }).map((_, i) => (
                        <Box key={i} sx={{
                          flex: 1, height: 8, borderRadius: '3px',
                          bgcolor: i < filledBoxes ? color : alpha(theme.palette.text.primary, 0.06),
                          boxShadow: i < filledBoxes ? `0 0 6px ${alpha(color, 0.4)}` : 'none',
                          transition: 'background-color 0.3s',
                        }} />
                      ))}
                    </Box>
                  </Box>
                </Tooltip>
              );
            })}
          </Box>
        </Box>
      </Stack>
      )}

      {/* Bottom Section - Security Findings with Threat Model Diagram */}
      {!hideFindings && (
      <Box>
        <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.5 }}>Security Findings</Typography>
        
        {/* Threat Model Diagram at top of findings */}
        {threatModelDiagram && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 13, fontWeight: 600, mb: 1.5 }}>Threat Model Diagram</Typography>
            <SmartDiagram chart={threatModelDiagram} legend="threat" />
          </Box>
        )}

        {/* Filter Chips */}
        {findings.length > 0 ? (
          <>
            <SecurityFindingsFilter
            filter={findingsFilter}
            setFilter={setFindingsFilter}
            findings={findings}
          />

          {/* Findings List (virtualized) */}
          {filteredFindings.length > 0 ? (
            <Virtuoso
              data={filteredFindings}
              defaultItemHeight={60}
              style={{ height: Math.min(filteredFindings.length * 65, 600) }}
              itemContent={(index, finding) => (
                <SecurityFindingAccordion
                  key={finding.id}
                  finding={finding}
                  index={index}
                  expandedPanel={expandedFindingPanel}
                  onAccordionChange={onFindingAccordionChange}
                  onAssignTask={handleAssignTaskClick}
                  isTaskDisabled={isTaskDisabled}
                />
              )}
            />
          ) : (
            <Alert severity="info" sx={{ mt: 1 }}>
              No findings match the selected filter.
            </Alert>
          )}
          </>
        ) : (
          /* Fallback to legacy display if no findings */
          <>
            {score.criticalFindings && score.criticalFindings.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'error.main', mb: 0.5 }}>Critical Findings ({score.criticalFindings.length})</Typography>
                <MarkdownContent content={score.criticalFindings.map((f: string) => `- ${f}`).join('\n')} />
              </Box>
            )}
            {score.topRecommendations && score.topRecommendations.length > 0 && (
              <Box>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: 'info.main', mb: 0.5 }}>Top Recommendations</Typography>
                <MarkdownContent content={score.topRecommendations.map((r: string) => `- ${r}`).join('\n')} />
              </Box>
            )}
          </>
        )}
      </Box>
      )}

      {/* Category Recommendations Dialog */}
      <CategoryRecommendationsDialog
        open={categoryDialogOpen}
        onClose={() => setSelectedCategory(null)}
        categoryKey={selectedCategory?.key || ''}
        categoryDisplayName={selectedCategory?.displayName || ''}
        score={selectedCategory?.score || 0}
        findings={findings}
      />

      {/* Assign Task Dialog */}
      {activeTaskFinding && (
        <SecurityTaskDialog
          open={!!activeTaskFinding}
          onClose={() => setActiveTaskFinding(null)}
          findingId={activeTaskFinding.id}
          findingText={activeTaskFinding.title || activeTaskFinding.finding}
          category={activeTaskFinding.category}
          appId={appId}
          onSubmit={handleTaskSubmit}
        />
      )}

      <Snackbar
        open={taskSnackbar.open}
        autoHideDuration={5000}
        onClose={() => setTaskSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setTaskSnackbar((prev) => ({ ...prev, open: false }))}
          severity={taskSnackbar.severity}
          sx={{ width: '100%' }}
        >
          {taskSnackbar.message}
        </Alert>
      </Snackbar>
    </Stack>
  );
}

export default SecurityScoreCard;
